import React, { useEffect, useState } from 'react';
import DefaultLayout from '../layouts/DefaultLayout';
import {
    PageSection,
    CustomHomepageGrid,
    PrimaryCallOut,
    FilterProvider,
    ReferencesBlock,
} from '../components';
import { jobCodes } from '../util/data/jobCodes';
import qlexLogo from '../assets/keytruda-qlex-logo.svg';
import { useGroup } from '../util/hooks';
 
// SEO INFORMATION
const pageMeta = {
    
    title: `KEYTRUDA® (pembrolizumab) | Official Website for Health Care Professionals`,
    keywords: `keytruda, pembrolizumab`,
    description: `Healthcare professionals may find information on FDA-approved indications for KEYTRUDA® (pembrolizumab) and KEYTRUDA QLEX™ (pembrolizumab and berahyaluronidase alfa-pmph), along with related KEYNOTE trials.`,
    schemaJsonLD: [
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/","@type":"MedicalAudience","audienceType":"US Health Care Professionals","geographicArea":"This site is intended only for residents of the United States and its territories, including Puerto Rico."}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/","@type":"Substance","name":"KEYTRUDA QLEX™","alternateName":"pembrolizumab and berahyaluronidase alfa-pmph","description":"Ketruda QLEX is approved for use in adult patients across solid tumor indications for KETRUDA-whether alone or in combination with other therapies.","activeIngredient":"pembrolizumab and berahyaluronidase alfa-pmph","recognizingAuthority":"Merck"}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/","@type":"Substance","name":"KEYTRUDA®","alternateName":"pembrolizumab","description":"KEYTRUDA is approved for 42 indications across 18 types of cancer. One indication is for the treatment of patients with unresectable or metastatic melanoma.","activeIngredient":"pembrolizumab","recognizingAuthority":"Merck"}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/","@type":"MedicalWebPage","name":"A Key to More Possibilities for Treating Your Patients:","description":"KEYTRUDA is approved for 42 indications across 18 types of cancer. "}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/","@type":"WebSite","name":"Keytrudahcp.com"}`
  ]
}

const cards = {
    'pi': {
        image: () => <img src="/static/images/badge-bookmark.png" alt="" width={60} height={60} />,
        title: 'Interactive Prescribing Information',
        url: '/prescribing-information/',
        target: '_blank',
        buttonLabel: 'View Prescribing Information',
        // secondButtonLabel: 'View KEYTRUDA QSUB Prescribing Information',
        // secondButtonHref: '',
        verticalCenter: 'center'
    },
    'dosing': {
        image: () => <img src="/static/images/badge-iv.png" alt="" width={60} height={60} quality={90} />,
        category: 'Dosing & Preparation',
        title: 'Dosing Options',
        url: '/dosing/options/',
        buttonLabel: 'View Dosing Details',
    },
    'safety': {
        image: () => <img src="/static/images/badge-safety.png" alt="" width={60} height={60} quality={90} />,
        category: 'Safety',
        title: 'Selected Adverse Reactions',
        url: '/safety/adverse-reactions/',
        buttonLabel: 'View More Information',
    },
    'resources': {
        image: () => <img src="/static/images/badge-info2.png" alt="" width={60} height={60} quality={90} />,
        category: 'Resources',
        title: 'Download Guides and Support Information',
        url: '/resources/hcp-and-patient-resources/',
        buttonLabel: 'View HCP & Patient Resources',
    },
    'combo': {
        title: 'Another Combination Treatment With KEYTRUDA® (pembrolizumab)',
        body:'Learn about another important indication for a combination treatment for your appropriate patients.',
        url: 'https://www.welireghcp.com/adjuvant-renal-cell-carcinoma/',
        buttonLabel: 'Visit Site',
    },
    'pd-l1': {
        category: 'Biomarker Testing',
        title: 'PD-L1 Testing & Scoring',
        body: 'Biomarker status can be a road map for personalizing treatment.',
        url: '/biomarker-testing/pd-l1/',
        buttonLabel: 'View Testing & Scoring',
    },
};


const tieredCTAData = {
    default: [
        {
            type: '50/50',
            rowTier: '2',
            items: [ cards.pi, cards.dosing ],
        },
        {
            type: '50/50',
            rowTier: '2',
            items: [ cards.safety, cards.resources ],
        },
        {
            type: '50/50',
            rowTier: '3',
            items: [ cards.combo, cards['pd-l1'] ],
        },
    ],
};
const tenYearsOfKeytruda = {
    imageUrl: "/static/images/10YearsKeytruda.svg",
    imageAlt:'10 Years of KEYTRUDA® (pembrolizumab)',
    body: `Initial FDA accelerated approval in certain patients with unresectable or metastatic melanoma on <span style="text-wrap:nowrap">September 4, 2014.</span>`,
    url: '/prescribing-information/',
    buttonLabel: 'View Prescribing Information',
  };

const keytrudaQSubCard = {
    backgroundImg: "/static/images/SubQBG.png",
    headerText: "FDA APPROVED",
    content: <><span className='mb-16'>KEYTRUDA QLEX is approved for use in adult patients across solid tumor indications for KEYTRUDA—whether alone or in combination with other therapies.</span>One such indication is for the adjuvant treatment of adult patients with renal cell carcinoma (RCC) at intermediate-high or high risk of recurrence following nephrectomy, or following nephrectomy and resection of metastatic lesions.</>,
    buttonLabel: "Learn More",
    subQicon: qlexLogo,
    subQAltText: "KEYTRUDA QLEX™ (pembrolizumab and berahyaluronidase alfa-pmph) Logo",
}

const TNBCReference = [{label: '1.', text: 'Referenced with permission from the NCCN Clinical Practice Guidelines in Oncology (NCCN Guidelines®) for Breast Cancer V.6.2026. © National Comprehensive Cancer Network, Inc. 2026. All rights reserved. Accessed July 29, 2026. To view the most recent and complete version of the guideline, go online to NCCN.org.'}]

const Index = ({ location }) => {
// This is temporary and will be changed for all custom pages if requested by client
    const [customTNBC, setCustomTNBC] = useState(false);
    const hash = location.hash.substring(1);

    const group = useGroup();

    const jobCode = group === 'bre'
    ? jobCodes[26].jobCode
    : jobCodes[0].jobCode;

    // useEffect(()=>{
    //     if(hash){            
    //         console.log('hash', hash);
    //         setTimeout(()=>{
    //             const element = document.querySelector(hash);
    //                 if(element){
    //                 element.scrollIntoView({ behavior: "smooth", block: "start" }); 
    //             }
    //         },100)          
    //     }
    // },[hash])

    return (
        <FilterProvider>
            <DefaultLayout jobCode={jobCode} pageMeta={pageMeta} location={location}>
                <div data-template-name="template-d">
                    {/* Begin Main Column */}                    

                    <CustomHomepageGrid tieredCtaRows={tieredCTAData.default} heroSubQ={keytrudaQSubCard} setCustomTNBC={setCustomTNBC} />

                    <PageSection bgColor="gradient">
                        <PrimaryCallOut
                            title="Mechanism of Action"
                            content="See how KEYTRUDA® (pembrolizumab) works."
                            primaryAction={{ label: 'View MOA', url: '/resources/mechanism-of-action/' }}
                        />
                    </PageSection>
                    <PageSection bgColor={customTNBC ? "cloud" : "mist"}>
                        <PrimaryCallOut
                            title="Patient Access & Reimbursement Support"
                            contentMaxWidth={500}
                            content="The Merck Access Program may help answer questions about billing and coding for KEYTRUDA, and co-pay assistance for eligible patients."
                            secondaryAction={{ label: 'Learn About The Merck Access Program', url: 'https://www.merckaccessprogram-keytruda.com/' }}
                        />
                    </PageSection>
                  {customTNBC &&  <PageSection  bgColor="mist">
                        <ReferencesBlock references={TNBCReference} />
                    </PageSection>}
                    {/* End Main Column */}
                    <div className="AWS-test-v2"></div>
                </div>
            </DefaultLayout>
        </FilterProvider>
    )
};

export default Index;
